import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@toki/auth';
import type { Category, Area } from '../services/api';
import { listCategories, createCategory, updateCategory, deleteCategory, listAreas } from '../services/api';
import { useDialog } from '../components/useDialog';
import styles from './Categorias.module.css';

export function CategoriasScreen() {
  const { provider } = useAuth();
  const { confirm: askConfirm, element: dialogElement } = useDialog();
  const [categories, setCategories] = useState<Category[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState('');
  const [icon, setIcon] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [defaultAreaId, setDefaultAreaId] = useState<string>('');

  const load = useCallback(async () => {
    const token = await provider.getAccessToken();
    if (!token) return;
    setLoading(true);
    try {
      const [cats, a] = await Promise.all([listCategories(token), listAreas(token)]);
      setCategories(cats);
      setAreas(a.filter((x) => x.isActive));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [provider]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setIcon('');
    setSortOrder(categories.length + 1);
    setDefaultAreaId('');
    setShowForm(true);
    setError(null);
  };

  const openEdit = (cat: Category) => {
    setEditing(cat);
    setName(cat.name);
    setIcon(cat.icon);
    setSortOrder(cat.sortOrder);
    setDefaultAreaId(cat.defaultAreaId ?? '');
    setShowForm(true);
    setError(null);
  };

  const handleSave = async () => {
    const token = await provider.getAccessToken();
    if (!token) return;
    setSaving(true);
    setError(null);
    try {
      const data = {
        name,
        icon,
        sortOrder,
        defaultAreaId: defaultAreaId || null,
      };
      if (editing) {
        await updateCategory(editing.id, data, token);
      } else {
        await createCategory(data, token);
      }
      setShowForm(false);
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cat: Category) => {
    const ok = await askConfirm({
      title: 'Eliminar categoría',
      message: `¿Eliminar la categoría "${cat.name}"?`,
      confirmLabel: 'Eliminar',
      danger: true,
    });
    if (!ok) return;
    const token = await provider.getAccessToken();
    if (!token) return;
    try {
      await deleteCategory(cat.id, token);
      await load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const areaName = (id: string | null) => {
    if (!id) return '—';
    return areas.find((a) => a.id === id)?.name ?? id;
  };

  if (loading) return <div className={styles.loading}>Cargando categorias...</div>;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Categorías</h2>
        <button className={styles.createBtn} onClick={openCreate}>+ Nueva categoría</button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {showForm && (
        <div className={styles.form}>
          <h3>{editing ? 'Editar categoría' : 'Nueva categoría'}</h3>
          <div className={styles.formRow}>
            <input className={styles.input} placeholder="Icono (emoji)" value={icon} onChange={(e) => setIcon(e.target.value)} style={{ width: 80 }} />
            <input className={styles.input} placeholder="Nombre" value={name} onChange={(e) => setName(e.target.value)} style={{ flex: 1 }} />
            <input className={styles.input} placeholder="Orden" type="number" value={sortOrder} onChange={(e) => setSortOrder(Number(e.target.value))} style={{ width: 80 }} />
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.label}>Area por defecto (auto-asignacion)</label>
            <select className={styles.select} value={defaultAreaId} onChange={(e) => setDefaultAreaId(e.target.value)}>
              <option value="">Sin equipo por defecto</option>
              {areas.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
          <div className={styles.formActions}>
            <button className={styles.saveBtn} onClick={handleSave} disabled={saving || !name.trim() || !icon.trim()}>
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
            <button className={styles.cancelBtn} onClick={() => setShowForm(false)}>Cancelar</button>
          </div>
        </div>
      )}

      <div className={styles.tableCard}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Icono</th>
            <th>Nombre</th>
            <th>Orden</th>
            <th>Equipo por defecto</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {categories.map((cat) => (
            <tr key={cat.id}>
              <td className={styles.iconCell}>{cat.icon}</td>
              <td>{cat.name}</td>
              <td>{cat.sortOrder}</td>
              <td>{areaName(cat.defaultAreaId)}</td>
              <td className={styles.actions}>
                <button className={styles.editBtn} onClick={() => openEdit(cat)}>Editar</button>
                <button className={styles.deleteBtn} onClick={() => handleDelete(cat)}>Eliminar</button>
              </td>
            </tr>
          ))}
          {categories.length === 0 && (
            <tr><td colSpan={5} className={styles.empty}>No hay categorias</td></tr>
          )}
        </tbody>
      </table>
      </div>
      {dialogElement}
    </div>
  );
}
